import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Loading } from '../components/Loading';
import { toast } from 'sonner';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { ImageCarousel } from '../components/ImageCarousel';
import app from '../firebase';

const db = getFirestore(app);

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const productDoc = doc(db, 'products', id);
      const productSnap = await getDoc(productDoc);
      
      if (productSnap.exists()) {
        const data = productSnap.data();
        if (data.isActive === false) {
          toast.error('Producto no disponible');
          navigate('/products');
        } else {
          setProduct({ id: productSnap.id, ...data });
        }
      } else {
        toast.error('Producto no encontrado');
        navigate('/products');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Error al cargar el producto');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${quantity} ${product.name} agregado(s) al carrito`);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Loading />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-[#00C2CB] hover:text-[#00C2CB]/80 font-quicksand font-semibold mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Volver
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="relative">
            <div className="aspect-square rounded-3xl overflow-hidden bg-white shadow-xl">
              <ImageCarousel 
                images={product.images && product.images.length > 0 ? product.images : [product.imageUrl]} 
                altText={product.name}
              />
            </div>
            {product.featured && (
              <div className="absolute top-6 right-6 bg-[#FFD700] text-[#3D2E00] px-4 py-2 rounded-full font-fredoka font-bold">
                Destacado
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <h1 className="text-4xl sm:text-5xl font-fredoka font-bold text-[#1A1A2E] mb-4" data-testid="product-detail-name">
              {product.name}
            </h1>
            <p className="text-3xl font-fredoka font-bold text-[#00C2CB] mb-6" data-testid="product-detail-price">
              ${product.price.toLocaleString()}
            </p>

            <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
              <h3 className="font-fredoka font-bold text-lg text-[#1A1A2E] mb-3">Descripción</h3>
              <p className="text-[#4A4A6A] font-quicksand leading-relaxed" data-testid="product-detail-description">
                {product.description}
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Package className="w-5 h-5 text-[#00C2CB] mr-2" />
                  <span className="font-quicksand font-semibold text-[#1A1A2E]">Disponibilidad:</span>
                </div>
                {product.stock > 0 ? (
                  <span className="text-green-600 font-quicksand font-semibold" data-testid="stock-status">
                    En stock ({product.stock} disponibles)
                  </span>
                ) : (
                  <span className="text-red-600 font-quicksand font-semibold" data-testid="stock-status">
                    Agotado
                  </span>
                )}
              </div>

              {product.stock > 0 && (
                <div className="flex items-center gap-4">
                  <label className="font-quicksand font-semibold text-[#1A1A2E]">Cantidad:</label>
                  <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 font-quicksand font-bold"
                      data-testid="decrease-quantity"
                    >
                      -
                    </button>
                    <span className="px-6 py-2 font-quicksand font-bold" data-testid="quantity-value">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 font-quicksand font-bold"
                      data-testid="increase-quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleAddToCart}
              disabled={product.stock <= 0}
              className="w-full bg-[#FF6B00] text-white px-8 py-4 rounded-full font-fredoka font-bold text-lg hover:bg-[#FF6B00]/90 hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              data-testid="add-to-cart-detail-button"
            >
              <ShoppingCart className="inline-block w-6 h-6 mr-2" />
              {product.stock > 0 ? 'Agregar al Carrito' : 'Producto Agotado'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};